import * as React from "react";
import { cn } from "../../lib/utils";

type FieldContextValue = {
  id: string;
  descriptionId?: string;
  invalid: boolean;
};
const FieldContext = React.createContext<FieldContextValue | null>(null);
export function useFieldProps(props: {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
}) {
  const field = React.useContext(FieldContext);
  return {
    id: props.id || field?.id,
    "aria-describedby":
      [props["aria-describedby"], field?.descriptionId]
        .filter(Boolean)
        .join(" ") || undefined,
    "aria-invalid": props["aria-invalid"] ?? (field?.invalid || undefined),
  };
}

export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  const field = useFieldProps(props);
  return (
    <input
      ref={ref}
      className={cn("vl-input", className)}
      {...props}
      {...field}
    />
  );
});
Input.displayName = "Input";
export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => {
  const field = useFieldProps(props);
  return (
    <textarea
      ref={ref}
      className={cn("vl-input vl-textarea", className)}
      {...props}
      {...field}
    />
  );
});
Textarea.displayName = "Textarea";
export const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label ref={ref} className={cn("vl-label", className)} {...props} />
));
Label.displayName = "Label";
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const generatedId = React.useId();
  const firstControl = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.props.id,
  ) as React.ReactElement<{ id?: string }> | undefined;
  const id = htmlFor || firstControl?.props.id || generatedId;
  const descriptionId = error || hint ? `${id}-description` : undefined;
  return (
    <FieldContext.Provider
      value={{ id, descriptionId, invalid: Boolean(error) }}
    >
      <div className={cn("vl-field", className)}>
        <Label htmlFor={id}>{label}</Label>
        {children}
        {error ? (
          <p id={descriptionId} className="vl-field-error" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p id={descriptionId} className="vl-hint">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
